import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Film } from 'lucide-react';

const VIDEOS = [
  {
    id: 'cjAKv8mOdQI',
    start: 0,
    tag: { tr: 'Tanıtım Filmi · 2026', en: 'Promotional Film · 2026' },
    title: {
      tr: 'Hacettepe Üniversitesi · Resmi Tanıtım Filmi 2026',
      en: 'Hacettepe University · Official Promotional Film 2026',
    },
  },
  {
    id: 'kNXXTs2QoM0',
    start: 59,
    tag: { tr: 'Kimya Bölümü Tanıtımı', en: 'Chemistry Department Introduction' },
    title: {
      tr: 'Moleküler Hafızadan Gerçek Dünya Problemlerine Çözümler',
      en: 'From Molecular Memory to Real-World Solutions',
    },
  },
];

const VideoCard = ({ video, isEn }) => {
  const [playing, setPlaying] = useState(false);
  const thumbnail = `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`;
  const title = isEn ? video.title.en : video.title.tr;
  const tag = isEn ? video.tag.en : video.tag.tr;
  const embedSrc = `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0${video.start ? `&start=${video.start}` : ''}`;

  return (
    <motion.div
      className="relative rounded-3xl overflow-hidden"
      style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)' }}
      initial={{ opacity: 0, y: 32, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedSrc}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full h-full cursor-pointer"
            aria-label={isEn ? `Play: ${title}` : `Oynat: ${title}`}
          >
            <img
              src={thumbnail}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />

            {/* Oynat butonu */}
            <span className="absolute inset-0 flex items-center justify-center">
              <motion.span
                className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #7B0D1E, #b01228)',
                  boxShadow: '0 10px 40px rgba(123,13,30,0.6), inset 0 1px 0 rgba(255,255,255,0.2)',
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(123,13,30,0.45)' }} />
                <Play className="w-7 h-7 md:w-8 md:h-8 text-white relative z-10" style={{ marginLeft: '3px' }} fill="white" />
              </motion.span>
            </span>

            {/* Alt bilgi şeridi */}
            <span className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex flex-col items-start gap-1.5 text-left">
              <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: '#fca5a5' }}>
                {tag}
              </span>
              <span className="text-white font-bold text-sm md:text-base leading-snug">
                {title}
              </span>
            </span>
          </button>
        )}
      </div>
    </motion.div>
  );
};

const PromoVideoSection = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  return (
    <section className="bg-white py-20 md:py-24 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Başlık */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] mb-5" style={{ color: '#7B0D1E' }}>
            <Film className="w-3.5 h-3.5" />
            {isEn ? 'Videos' : 'Videolar'}
          </span>
          <h2 className="font-black text-gray-900 leading-tight" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)' }}>
            {isEn
              ? <>Hacettepe University<br /><span style={{ color: '#7B0D1E' }}>Promotional Films</span></>
              : <>Hacettepe Üniversitesi<br /><span style={{ color: '#7B0D1E' }}>Tanıtım Filmleri</span></>
            }
          </h2>
          <p className="text-gray-500 text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
            {isEn
              ? "Get to know our campus, academic community and student life through Hacettepe University's official 2026 promotional film."
              : "Hacettepe Üniversitesi'nin 2026 yılı resmi tanıtım filmiyle kampüsümüzü, akademik camiamızı ve öğrenci yaşamını daha yakından tanıyın."
            }
          </p>
        </motion.div>

        {/* Video kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {VIDEOS.map(video => (
            <VideoCard key={video.id} video={video} isEn={isEn} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default PromoVideoSection;
